import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBarProps } from '../../../types/dashboardV2';
import { FilterBar as GenericFilterBar } from '../../../components/common/FilterBar/FilterBar';
import { StatusFilterDropdown } from '../../../components/common/StatusFilterDropdown/StatusFilterDropdown';
import { FilterToggle } from '../../../components/common/FilterToggle/FilterToggle';
import {
  STATION_STATUS_OPTIONS,
  CONNECTOR_TYPE_OPTIONS,
} from '../../../constants/filterOptions';
import { StationStatus } from '../../../shared/enums/status';
import { ConnectorTypeEnum } from '../../../shared/enums/connector-type';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOrganizationsQuery } from '../../../hooks/useDashboardQueries';
import { ORGANIZATIONS_LIST_PAGE_SIZE } from '../../../constants/organization';

export const FilterBar = memo(function FilterBar({
  filters,
  onFilterChange,
  onRefresh,
  onAddStation,
  organizationId,
  onOrganizationChange,
}: FilterBarProps) {
  const { t } = useTranslation();
  const { has, organization } = usePermissions();
  // Org owners and members only ever see their own organization.
  const showOrganizationFilter = has('organizations.view') && !organization;
  const { data: organizations } = useOrganizationsQuery(
    1,
    ORGANIZATIONS_LIST_PAGE_SIZE,
    '',
    showOrganizationFilter,
  );

  const handleStatusChange = useCallback(
    (status: StationStatus | '') => onFilterChange({ status }),
    [onFilterChange],
  );

  const handleConnectorTypeChange = useCallback(
    (connectorType: ConnectorTypeEnum | '') => onFilterChange({ connectorType }),
    [onFilterChange],
  );

  const handleSearchChange = useCallback(
    (search: string) => onFilterChange({ search }),
    [onFilterChange],
  );

  const handleOpenNowToggle = useCallback(
    (openNow: boolean) => onFilterChange({ openNow }),
    [onFilterChange],
  );

  return (
    <GenericFilterBar
      search={{
        value: filters.search,
        onChange: handleSearchChange,
        placeholder: t('dashboard.stations.searchPlaceholder'),
        ariaLabel: t('dashboard.stations.searchAria'),
      }}
      controls={
        <>
          <StatusFilterDropdown
            value={filters.status as StationStatus | ''}
            options={STATION_STATUS_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel={t('dashboard.filters.aria.filterStationsStatus')}
          />
          <StatusFilterDropdown
            value={filters.connectorType as ConnectorTypeEnum | ''}
            options={CONNECTOR_TYPE_OPTIONS}
            onChange={handleConnectorTypeChange}
            ariaLabel={t('dashboard.filters.aria.filterStationsConnector')}
            iconName="ev_station"
          />
          <FilterToggle
            label={t('dashboard.filters.toggles.openNow')}
            value={filters.openNow}
            onChange={handleOpenNowToggle}
            icon="schedule"
          />
          {showOrganizationFilter && (
            <select
              className="select-filter"
              value={organizationId ?? ''}
              onChange={(e) => onOrganizationChange(e.target.value || undefined)}
              aria-label={t('dashboard.filters.allOrganizations')}
            >
              <option value="">{t('dashboard.filters.allOrganizations')}</option>
              {(organizations?.data ?? []).map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}
        </>
      }
      privacyScope="stations"
      onRefresh={onRefresh}
      refreshAriaLabel={t('dashboard.filters.aria.refreshStations')}
      rightAction={
        <button type="button" onClick={onAddStation} className="add-station-btn">
          <span className="material-symbols-outlined" aria-hidden="true">add</span>
          {t('dashboard.stations.addStation')}
        </button>
      }
    />
  );
});

export default FilterBar;
